/* eslint-disable react-hooks/exhaustive-deps */
import React, { forwardRef, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GridConfig } from "./features/config/Customer.data";

/** Common Components */
import MolGrid from "../../components/grid/MolGrid";
import CardSection from "../../components/ui/card/CardSection";
import Pagination from "../../components/ui/pagination/Pagination";
import Iconify from "../../layouts/components/iconify/iconify";
import Avatar from "../../components/ui/avatar/Avtar";
import { DropdownWrapper } from "../../components/ui/dropdownwrapper/DropdownWrapper";

/** Common Services & Data Files */
import ToastService from "../../services/toastService/ToastService";
import SwalAlert from "../../services/swalService/SwalService";

/** RTK Query */
import {
  useDeleteCustomerMutation,
  useGetCustomersMutation,
} from "../../app/services/customerAPI";

/** Custom Hooks */
import useDebounce from "../../app/customHooks/useDebounce";

/** Utilities */
import { encryptUrlData } from "../../services/CryptoService";

/** CSS Files */
import "./Customer.scss";

const Customer = forwardRef((props, ref) => {
  const molGridRef = useRef();
  const [listData, setListData] = useState();
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [searchText, setSearchText] = useState("");

  const [viewType, setViewType] = useState(
    localStorage.getItem("viewType") || "list"
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const debouncedSearch = useDebounce(searchText, 300);

  const navigate = useNavigate();
  const { confirm } = SwalAlert();

  //** API Call's */
  const [
    getList,
    { isLoading: isListLoading, isSuccess: isListSuccess, data: isListData },
  ] = useGetCustomersMutation();

  const [
    deleteCustomer,
    {
      isSuccess: isDeleteSuccess,
      isLoading: isCustomerDeleting,
      data: isDeletData,
    },
  ] = useDeleteCustomerMutation();

  //** Funcation's */
  const getLists = (pageObject) => {
    getList(pageObject);
  };

  const handlePageChange = (page) => {
    const newPage = page.pageNumber || page;
    const newPageSize = page.pageSize || pageSize;

    setCurrentPage(newPage);
    setPageSize(newPageSize);

    localStorage.setItem("currentPage", newPage);
    localStorage.setItem("pageSize", newPageSize);

    const request = {
      pagination: { pageNumber: newPage, pageSize: newPageSize },
      filters: { searchText: debouncedSearch },
    };
    getLists(request);
  };

  //** Use Effects */
  useEffect(() => {
    if (isListSuccess && isListData) {
      if (isListData) {
        setListData(isListData.dataSource);
      }
      if (isListData.totalRecord) {
        setTotalRowCount(isListData.totalRecord);
      }
    }
  }, [isListSuccess, isListData]);

  useEffect(() => {
    if (molGridRef.current) {
      const currentPageObject = molGridRef.current.getCurrentPageObject();
      const request = {
        pagination: {
          pageNumber: currentPageObject.pageNumber,
          pageSize: currentPageObject.pageSize,
        },
        filters: { searchText: debouncedSearch },
      };
      getLists(request);
    }
  }, [debouncedSearch, pageSize]);

  useEffect(() => {
    handlePageChange({ pageNumber: currentPage, pageSize });
  }, [debouncedSearch, currentPage, pageSize]);

  useEffect(() => {
    if (viewType === "card" && !listData?.length) {
      getLists({
        pagination: { pageNumber: currentPage, pageSize: pageSize },
        filters: { searchText: debouncedSearch },
      });
    }
  }, [viewType, currentPage, debouncedSearch]);

  useEffect(() => {
    if (isDeleteSuccess && isDeletData) {
      ToastService.success(isDeletData.errorMessage);
      handlePageChange({ pageNumber: currentPage, pageSize });
    }
  }, [isDeleteSuccess, isDeletData]);

  //** Action Handler */
  const handleEditClick = (data) => {
    localStorage.setItem("currentPage", currentPage);
    localStorage.setItem("pageSize", pageSize);
    navigate(`/admin/EditCustomer/${encryptUrlData(data.customerId)}`);
  };

  const handleDeleteClick = (data) => {
    confirm(
      "Delete?",
      "Are you sure you want to Delete?",
      "Delete",
      "Cancel"
    ).then((confirmed) => {
      if (confirmed) {
        deleteCustomer(data.customerId);
      }
    });
  };

  const actionHandler = {
    EDIT: handleEditClick,
    DELETE: handleDeleteClick,
  };

  const handleOnSearchChange = (e) => {
    let value = e.target.value;
    setSearchText(value);
  };

  const addCustomer = () => {
    navigate("/admin/AddCustomer");
  };

  const showListView = () => {
    setViewType("list");
    localStorage.setItem("viewType", "list");
  };
  const showCardView = () => {
    setViewType("card");
    localStorage.setItem("viewType", "card");
  };

  const getCompanyWebUrl = (customer) => {
    if (!customer?.companyWebUrl) return '#';
    return customer.companyWebUrl.startsWith('http')
      ? customer.companyWebUrl
      : `https://${customer.companyWebUrl}`;
  }

  return (
    <div className="table-style">
      <div className="customer-list center-top-space">
        <div className="main-filter-section">
          <CardSection
            isContentLoading={isListLoading || isCustomerDeleting}
            cardTitle={"Customer List"}
            rightButton={true}
            buttonClassName="theme-btn"
            titleButtonClick={addCustomer}
            buttonText="Customer"
            textWithIcon={true}
            iconName="material-symbols:add-rounded"
            fontIcon={true}
            searchInputName="Search by Customer Name"
            searchInput={true}
            handleChange={handleOnSearchChange}
            isShorting={true}
            showListView={showListView}
            showCardView={showCardView}
            viewType={viewType}
          >
            {viewType === "card" ? (
              <div className="row customer-card-layout">
                {listData?.map((customer, index) => (
                  <div className="ciustomer-card-main" key={index + 1}>
                    <div className="customer-card-view">
                      <div className="customer-card-header">
                        <div className="customer-card-header-profile">
                          <Avatar
                            alt={customer.companyName || "Company Name"}
                            shape="square"
                            size="medium"
                          />
                          {customer.isActive === true ? (
                            <div className="customer-status active"></div>
                          ) : (
                            ""
                          )}

                          <div className="customer-card-header-title">
                            <div className="company-name">
                              {customer.companyName}
                            </div>
                            <a
                              className="customer-link"
                              href={getCompanyWebUrl(customer)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Iconify icon="ri:share-box-line" width={15} />{" "}
                              <div className="customer-link-text">
                                {customer.companyWebUrl || "Website"}
                              </div>
                            </a>
                          </div>
                        </div>
                        <div className="">
                          <DropdownWrapper
                            direction="left"
                            className="action-button-dropdown"
                            toggleElement={
                              <button className="customer-more-btn">
                                <Iconify icon="humbleicons:dots-vertical" />
                              </button>
                            }
                          >
                            <button onClick={() => handleEditClick(customer)}>
                              <Iconify icon="line-md:pencil" />
                              Edit
                            </button>
                            <button onClick={() => handleDeleteClick(customer)}>
                              <Iconify icon="mingcute:delete-2-line" />
                              Delete
                            </button>
                          </DropdownWrapper>
                        </div>
                      </div>
                      <div className="customer-card-body">
                        <div className="customer-card-info-title">
                          Customer Info :
                        </div>
                        <div className="customer-card-info-content">
                          <div className="customer-card-info">
                            <div className="customer-icon">
                              <Iconify icon="solar:user-broken" width={15} />
                            </div>
                            <div className="customer-name">
                              {customer.customerName}
                            </div>
                          </div>
                          <div className="customer-card-info">
                            <div className="customer-icon">
                              <Iconify icon="weui:email-outlined" width={15} />
                            </div>
                            <div className="customer-name">
                              {customer.primaryEmail ? customer.primaryEmail : "N/A"}
                            </div>
                          </div>
                          <div className="customer-card-info">
                            <div className="customer-icon">
                              <Iconify
                                icon="mdi:address-marker-outline"
                                width={15}
                              />
                            </div>
                            <div className="customer-name">{customer.city ? customer.city : "N/A"}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="table-grid">
                <MolGrid
                  ref={molGridRef}
                  configuration={GridConfig}
                  dataSource={listData}
                  isLoading={isListLoading || isCustomerDeleting}
                  pagination={{
                    totalCount: totalRowCount,
                    pageSize: 20,
                    currentPage: 1,
                  }}
                  onPageChange={handlePageChange}
                  onActionChange={actionHandler}
                />
              </div>
            )}
          </CardSection>
          {viewType === "card" ? (
            <div className="pagination-container">
              <Pagination
                currentPage={currentPage}
                pageSize={pageSize}
                totalCount={totalRowCount}
                onPageChange={handlePageChange}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
});

export default Customer;
